import React, { useEffect, useRef, useMemo } from 'react'
import cn from 'classnames'
import mapKeys from 'lodash/mapKeys'
import PropTypes from 'prop-types'
import { primaryInput } from 'detect-it'
import { createUseStyles } from '../helpers/createStyles'
import isEqual from 'lodash/isEqual'

const preventVerticalScrolling = (flickity) => {
  // This will prevent the vertical scrolling when scrolling in flickity
  flickity.on('dragStart', () => { document.ontouchmove = e => e.preventDefault() })
  flickity.on('dragEnd', () => { document.ontouchmove = () => true })
}

const getKey = (child) => {
  if (!child) return null
  const childKey = typeof child.key === 'number' ? child.key.toString() : child.key
  return childKey?.replace('.$', '')
}

const useDidChildrenChange = (children, check, func) => {
  const previousChildren = useRef()
  const didChangeRef = useRef()

  if (check) {
    const keys = React.Children.map(children, getKey)
    const prevKeys = previousChildren.current && React.Children.map(previousChildren.current, getKey)
    if (!isEqual(prevKeys, keys)) {
      didChangeRef.current = {} // we just want to trigger a re-render
      if (func) {
        func()
      }
    }
  }
  previousChildren.current = children
  return didChangeRef
}

const FlickityComponent = ({
  className, children, options,
  setFlickityRef, disabled = false,
  selectedIndex
}) => {
  const flickityRef = useRef(null)
  const flickityEl = useRef()
  const styles = useStyles()

  const flickityOptions = useMemo(() => ({
    autoPlay: false,
    draggable: true,
    pageDots: false,
    prevNextButtons: false,
    contain: true,
    accessibility: primaryInput !== 'touch', // This is to stop the jump on focus on mobile
    ...options
  }), [options])

  // Setup and destroy flickity
  useEffect(() => {
    // If we already have a flickity reference then the children must not of changed
    if (!disabled && !flickityRef.current) {
      const Flickity = require('flickity')
      const flick = new Flickity(
        flickityEl.current,
        flickityOptions
      )

      if (setFlickityRef) {
        setFlickityRef(flick)
      }

      // Call this before we resize so we get the correct x on the first load
      flick.resize()

      preventVerticalScrolling(flick)
      flick.on('dragMove', (event, pointer, moveVector) => {
        const dragMoveEvent = new window.CustomEvent('flickity-dragMove', { detail: event })
        window.dispatchEvent(dragMoveEvent)
      })

      flickityRef.current = flick
    }

    if (disabled && flickityRef.current) {
      flickityRef.current.destroy()
      flickityRef.current = null
    }
  }, [children, disabled])

  // Re adds the listeners when they change
  useEffect(() => {
    if (flickityRef.current) {
      if (options && options.on) {
        mapKeys(options.on, (value, key) => flickityRef.current.on(key, value))
      }
      flickityRef.current.resize()
    }
    return () => {
      if (flickityRef.current) {
        if (options && options.on) {
          mapKeys(options.on, (value, key) => flickityRef.current.off(key, value))
        }
      }
    }
  }, [flickityOptions])

  // Support changing option `contain` after mount
  useEffect(() => {
    if (flickityRef.current && flickityRef.current.cells) {
      flickityRef.current.option({ contain: flickityOptions.contain })
      flickityRef.current.reposition()
    }
  }, [flickityOptions.contain])

  // Flickity destructor
  useEffect(() => {
    return () => {
      if (flickityRef.current) {
        flickityRef.current.destroy()
      }
    }
  }, [])

  // Updates the selected index on flickity
  useEffect(() => {
    if (flickityRef.current) {
      flickityRef.current.select(selectedIndex)
    }
  }, [selectedIndex])

  // If the children have changed then we need to destroy flickity,
  // Flickity will update again in the side effect.
  // We need to do this in the render, as doing this in a effect will be too late

  useDidChildrenChange(
    children,
    !!flickityRef.current,
    () => {
      flickityRef.current.destroy()
      flickityRef.current = null
      if (setFlickityRef) {
        setFlickityRef(null)
      }
    }
  )

  return (
    <div className={cn(styles.container, className)} ref={flickityEl}>
      {children}
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    tapHighlightColor: 'transparent',
    userSelect: 'none',
    outline: 'none'
  }
})

FlickityComponent.propTypes = {
  options: PropTypes.shape({
    cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
    contain: PropTypes.bool,
    draggable: PropTypes.bool,
    on: PropTypes.object
  })
}

export default FlickityComponent
